/**
 * useUnseenNotifications — drives the notification bell's "something new arrived"
 * signalling: a swing that persists until the list is opened, and a chime that fires
 * once per arrival (WCO-2155).
 *
 * The rules are all about EDGES, not levels:
 *  - Fires on the count RISING. A standing count of 3 is not new — treating non-zero
 *    as "unseen" would leave the bell swinging and chiming forever.
 *  - A FALLING count (an alert resolved elsewhere, or dismissed in another tab) is
 *    not an arrival and must stay silent.
 *  - The first render only establishes a baseline, so a page load never chimes for
 *    notifications the manager has already seen.
 *  - Opening the list IS seeing them: the swing stops and only a later arrival
 *    restarts it.
 *
 * Previous count and open-state live in refs: they are bookkeeping, not render state.
 * Keeping isOpen out of the arrival effect's deps matters — including it would re-run
 * the effect on every open/close and re-evaluate the edge against a stale previous
 * count.
 */
import { useEffect, useRef, useState } from 'react';
import { playNotificationChime } from '../utils/notificationSound';

export interface UnseenNotificationsOptions {
  /** Play a sound on arrival. Default true. */
  sound?: boolean;
}

/**
 * @param notifCount current total notification count
 * @param isOpen     whether the notification list is currently open
 * @returns whether there are unseen notifications (drives the swing animation)
 */
export function useUnseenNotifications(
  notifCount: number,
  isOpen: boolean,
  options: UnseenNotificationsOptions = {}
): boolean {
  const { sound = true } = options;
  const [hasUnseen, setHasUnseen] = useState(false);

  const prevCount = useRef<number | null>(null);
  const isOpenRef = useRef(isOpen);
  const soundRef = useRef(sound);
  isOpenRef.current = isOpen;
  soundRef.current = sound;

  useEffect(() => {
    const prev = prevCount.current;
    prevCount.current = notifCount;

    if (prev === null) return;      // baseline only — never chime on first render
    if (notifCount <= prev) return; // unchanged, or resolved — not an arrival

    // An already-open dropdown means they are looking at the list right now.
    if (!isOpenRef.current) setHasUnseen(true);
    if (soundRef.current) playNotificationChime();
  }, [notifCount]);

  // Opening the list IS seeing them — stop swinging until the next arrival.
  useEffect(() => {
    if (isOpen) setHasUnseen(false);
  }, [isOpen]);

  return hasUnseen;
}

export default useUnseenNotifications;
